import { InputType, InputValueType, TagInputConfig, TagInputConfigRecord } from './TagInput.types';
import { batteryModeConfig, powerSourceConfig } from './tagInput.utils';
import { Autocomplete, AutocompleteProps, Checkbox, Chip, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const config: TagInputConfigRecord = {
  powerSource: powerSourceConfig,
  batteryMode: batteryModeConfig,
};

interface SimpleTagInputProps<T extends InputType> {
  fullWidth?: boolean;
  inputType: T;
  label?: string;
  onChange: (value: InputValueType<T>[]) => void;
  onDelete?: (option: string, index: number) => void;
  placeholder?: string;
  readOnly?: boolean;
  shrink?: boolean;
  value: InputValueType<T>[];
}

export const SimpleTagInput = <T extends InputType>({
  fullWidth,
  inputType,
  label,
  onChange,
  onDelete,
  placeholder,
  readOnly = false,
  shrink,
  value,
  ...props
}: SimpleTagInputProps<T> &
  Omit<AutocompleteProps<InputValueType<T>, true, false, true>, 'onChange' | 'options' | 'value' | 'renderInput'>) => {
  const { t } = useTranslation();
  const selectedConfig: TagInputConfig<InputValueType<T>> = config[inputType] as TagInputConfig<InputValueType<T>>;

  const { iconMapping, labelMapping, options } = selectedConfig;

  const renderTags = (tagValue: InputValueType<T>[]) =>
    tagValue.map((option, index) => {
      const IconComponent = iconMapping ? (iconMapping[option] as React.ElementType) : undefined;

      return (
        <Chip
          key={index}
          icon={IconComponent ? <IconComponent /> : undefined}
          label={t(labelMapping[option])}
          size="small"
          sx={{ m: 0.5 }}
          onDelete={onDelete ? () => onDelete(option, index) : undefined}
        />
      );
    });

  return (
    <Autocomplete
      {...props}
      multiple
      options={options as InputValueType<T>[]}
      value={(value as InputValueType<T>[]) ?? []}
      fullWidth={fullWidth}
      onChange={(_, newValue) => !readOnly && onChange(newValue as InputValueType<T>[])}
      disableCloseOnSelect
      getOptionLabel={(option) => t(labelMapping[option as InputValueType<T>])}
      isOptionEqualToValue={(option, val) => option === val}
      renderTags={renderTags}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={selected} />
            {t(labelMapping[option])}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          size="small"
          placeholder={!value.length ? placeholder : undefined}
          InputLabelProps={{ shrink }}
        />
      )}
    />
  );
};
